import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { DropdownOptionsResponse } from '@/@types/responses/common/DropdownOptionsResponse'
import { Caliber } from '@/@types/responses/caliber/Caliber'
import { CaliberDetails } from '@/@types/responses/caliber/CaliberDetails'
import { PagingResponse } from '@/@types/responses/common/PagingResponse'
import { CaliberMessage } from '@/@types/messages/CaliberMessage'
import { CartridgeMessage } from '@/@types/messages/CartridgeMessage'
import { MiscellaneousService } from './miscellaneous.service'
import { ObjectCreatedResponse } from '@/@types/responses/common/ObjectCreatedResponse'

export class CaliberService {
  private appConfig: AppConfig
  private miscellaneousService: MiscellaneousService

  constructor(miscellaneousService: MiscellaneousService) {
    this.appConfig = new AppConfig()
    this.miscellaneousService = miscellaneousService
  }

  public createCaliber(caliberMessage: CaliberMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(caliberMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/calibers`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createCartridge(caliberId: number, cartridgeMessage: CartridgeMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cartridgeMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/calibers/${caliberId}/cartridges`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public deleteCaliber(id: number): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/calibers/${id}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getCaliber(id: number): Promise<CaliberDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/calibers/${id}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return new CaliberDetails(json.id, json.boreDiameter, json.name, json.cartridges, json.createdByUser, json.createdDate, json.lastModifiedByUser, json.lastModifiedDate)
    })
  }

  public getCaliberDropdownOptions(): Promise<DropdownOptionsResponse<DropdownOptionResponse<number>>> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/calibers/dropdown-options`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as DropdownOptionsResponse<DropdownOptionResponse<number>>
    })
  }

  public searchCalibers(searchTerm: string, pageNumber: number, pageSize: number, sortField: string, ascending: boolean): Promise<PagingResponse<Caliber>> {
    const queryString = `?searchTerm=${searchTerm}&pageNumber=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&ascending=${ascending}`

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/calibers${queryString}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as PagingResponse<Caliber>
    })
  }

  public updateCaliber(caliberId: number, caliberMessage: CaliberMessage): Promise<void> {
    const requestOptions = {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(caliberMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/calibers/${caliberId}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
